/* eslint-disable no-param-reassign */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Nav, NavItem, Tab, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  filter,
  find,
  forEach,
  get,
  isArray,
  isEmpty,
  isEqual,
  keys,
  map,
  mapValues,
  some
} from 'lodash';

import { history } from './../../../store/configureStore';
import Filter from '../Filter';
import ActiveFilters from '../ActiveFilters';
import { FilterSetType, GroupingSetType } from '../filter-types';
import { LoadingSpinner } from '../../LoadingSpinner';
import AudienceSegment from '../../AudienceSegment';

import {
  clearFields,
  updateField,
  updateFieldBooleanSeparator,
  formatAllowedFilter,
  handleSegmentChange,
  getTitleFilterId,
  formatLabel
} from '../../../utils/filters';

import { integerValueFields, stringValueFields } from '../../../utils/shared';
import './TabbedFilters.css';
import './TabbedFilters.print.css';

class TabbedFilters extends React.Component {
  state = {
    groupings: this.props.groupings,
    hasChanges: false,
    selectedGroup: '',
    selectedSegment: {},
    fieldState: {},
    segments: this.props.segments
  };

  static getDerivedStateFromProps(props, state) {
    if (!state.fields && props.defaultState) {
      const { fields } = props;
      if (props.allowedDataCategories) {
        forEach(props.allowedDataCategories, restrictionString => {
          const [restrictedField, restriction] = restrictionString.split(':');
          if (restriction !== '*') {
            const values = restriction
              .split(',')
              .map(v => ({ label: formatLabel(restrictedField, v), value: v }));

            if (fields && fields[restrictedField]) {
              fields[restrictedField].value =
                values.length > 1 ? values : values[0];
            }
          }
        });
      }

      return {
        fields
      };
    }

    return state;
  }

  componentDidUpdate(prevProps, prevState) {
    const { selectedGroup } = this.state;
    const { autoApply, hideGroup, groupings, fields } = this.props;
    const hasNewGroupings = !isEqual(prevProps.groupings, groupings);
    const hasNewFields = !isEqual(prevProps.fields, fields);
    if (autoApply) {
      if (!isEmpty(selectedGroup)) {
        if (prevProps.hideGroup === false && hideGroup) {
          this.resetSelectGroup();
        } else {
          this.handleClickApply();
        }
      }
    }
    if (hasNewGroupings || hasNewFields) {
      const nextState = {};

      if (hasNewGroupings) {
        nextState.groupings = groupings;
      }

      if (hasNewFields) {
        nextState.fields = mapValues(fields, (fieldValue, fieldKey) => {
          const propsValue = get(fields, [fieldKey, 'value'], null);
          const stateValue = get(
            this.state,
            ['fields', fieldKey, 'value'],
            null
          );
          const bool = get(
            prevState,
            ['fields', fieldKey, 'booleanSeparator'],
            'OR'
          );
          const values =
            formatAllowedFilter(this.props.allowedDataCategories, fieldKey) ||
            [];

          return {
            booleanSeparator: bool,
            ...fieldValue,
            value:
              propsValue ||
              stateValue ||
              (values.length > 1 ? values : values[0])
          };
        });
      }
      this.setState(nextState); // eslint-disable-line react/no-did-update-set-state
    }
  }

  componentWillUnmount() {
    this.props.resetFilters();
  }

  onClickSaveFilters = () => {
    this.props.openAudienceSegmentWindow(
      null,
      '',
      Object.keys(this.props.fields).map(f => this.props.fields[f])
    );
  };

  handleChangeBooleanSeparator = (fieldId, boolean) => {
    const booleanSeparator = boolean === 'OR' ? 'AND' : 'OR';
    const fields = updateFieldBooleanSeparator(
      this.state.fields,
      fieldId,
      booleanSeparator
    );
    const fieldBooleanSeparator = get(
      this.props.fields,
      [fieldId, 'booleanSeparator'],
      false
    );
    const fieldValue = get(this.state.fields, [fieldId, 'value'], null);

    const changedFields = {
      ...this.state.changedFields,
      [fieldId]:
        !isEmpty(fieldValue) &&
        !isEqual(fieldBooleanSeparator, booleanSeparator)
    };

    const nextState = {
      fields,
      changedFields,
      hasChanges: !isEmpty(fieldValue) && !isEqual(this.props.fields, fields)
    };

    this.setState(nextState, () =>
      this.props.onChangeBooleanSeparator(fieldId, booleanSeparator, fields)
    );
  };

  handleClear = fieldId => {
    const fields = updateField(this.state.fields, fieldId, null);
    const nextState = { fields };

    this.setState(nextState, () => this.props.onClear(fieldId));
  };

  handleClearAll = () => {
    const nextState = {
      changedFields: {},
      fields: clearFields(this.state.fields, this.props.hideFilters),
      hasChanges: false
    };

    history.push({ search: '' });

    this.setState(nextState, () => {
      this.props.onClearAll(this.state.fields, this.props.hideFilters);
    });
  };

  handleApply = () => {
    const { fields } = this.state;

    const nextState = {
      changedFields: {},
      hasChanges: false
    };

    const filters = mapValues(fields, 'value');
    const booleanFilters = mapValues(fields, 'booleanSeparator');

    integerValueFields.forEach(e => {
      let setExcludedValues = [];
      if (filters[e] && isArray(filters[e])) {
        setExcludedValues = filters[e].map(obj => ({
          ...obj,
          value: (obj?.state === 'excluded' || obj.label.startsWith('Excludes:')
            ? `not ${obj.value}`
            : `${obj.value}`
          ).replace('not not', 'not'),
          base: obj.value
        }));

        filters[e] = [...setExcludedValues];
      }
    });

    stringValueFields.forEach(e => {
      let setExcludedValues = [];
      if (filters[e] && isArray(filters[e])) {
        setExcludedValues = filters[e].map(obj => ({
          ...obj,
          value: (obj?.state === 'excluded' || obj.label.startsWith('Excludes:')
            ? `-${obj.value}`
            : `${obj.value}`
          ).replace(/^(--)/, '-')
        }));
        filters[e] = [...setExcludedValues];
      }
    });

    this.setState(nextState, () =>
      this.props.onApply(filters, { syncWithUrl: true }, booleanFilters)
    );
  };

  handleClickApply = async () => {
    if (this.state.hasChanges) {
      await this.handleApply();
      if (!this.props.autoApply) {
        this.resetSelectGroup();
      }
    }
  };

  hasSampleBalanceFilter = countries =>
    !isEmpty(countries) &&
    Array.isArray(countries) &&
    !isEmpty(find(countries, country => country.value === 'United States'));

  resetSelectGroup = () => this.setState({ selectedGroup: '' });

  groupHasValue(group) {
    return some(
      group.filters,
      groupFilter => !isEmpty(get(this.state, `fields.${groupFilter}.value`))
    );
  }

  handleChange = (fieldId, value) => {
    let fields = updateField(this.state.fields, fieldId, value);
    const fieldValue = get(this.props.fields, [fieldId, 'value'], null);
    let segmentChange = false;
    let changedFields = {
      ...this.state.changedFields,
      [fieldId]:
        !(isEmpty(fieldValue) && isEmpty(value)) && !isEqual(fieldValue, value)
    };

    if (fieldId === 'segments') {
      const uuid = get(value, ['0', 'uuid'], null);
      const resetedFields = mapValues(fields, field => ({
        ...field,
        value: null
      }));
      const { newFields, newChangedFields } = handleSegmentChange(
        value,
        resetedFields,
        changedFields
      );
      fields = newFields;
      changedFields = newChangedFields;
      fields.segments.value = uuid;

      const segmentList = get(fields, ['segments', 'options'], []);
      const hasSegment = find(
        segmentList,
        segment => segment.name === get(value, [0, 'name'], false)
      );
      if (!hasSegment && get(fields, ['segments', 'options'], false)) {
        fields.segments.options = [...fields.segments.options, value[0]];
      }
      segmentChange = true;
    }

    const nextState = {
      fields,
      changedFields,
      hasChanges:
        Object.keys(changedFields).length && !isEqual(this.props.fields, fields)
    };

    if (segmentChange) {
      [nextState.selectedSegment] = value;
    }
    this.setState(nextState, () => this.props.onChange(fieldId, value, fields));
  };

  checkValidFilters(f) {
    return get(this.props, ['fields', f, 'label'], false);
  }

  renderApply() {
    const { loading } = this.props;
    const { changedFields, hasChanges } = this.state;

    const activeFilters = keys(filter(changedFields)).length;
    const props = {
      className: classnames('tvt-filters__btn', {
        'tvt-filters__btn--disabled': !hasChanges
      }),
      onClick: loading ? null : this.handleClickApply,
      disabled: !hasChanges
    };

    return (
      <button {...props}>
        {loading && <LoadingSpinner />}
        {!loading && <>Apply {activeFilters ? `(${activeFilters})` : ''}</>}
      </button>
    );
  }

  renderGroup = group => {
    const className = classnames('tvt-filters__tab', {
      'tvt-filters__tab--has-value': this.groupHasValue(group)
    });

    const hasOnlyHiddenFilters = group.filters.every(f =>
      this.props.hideFilters.includes(f)
    );

    if (hasOnlyHiddenFilters) {
      return null;
    }

    return (
      <NavItem className={className} eventKey={group.id} key={group.id}>
        {group.label}
      </NavItem>
    );
  };

  renderGroupContent = group => {
    return (
      <Tab.Pane eventKey={group.id} key={group.id}>
        {map(group.filters, this.renderFilter)}
      </Tab.Pane>
    );
  };

  renderFilter = fieldId => {
    if (this.props.hideFilters.includes(fieldId)) {
      return null;
    }
    const filterField = get(this.state, `fields.${fieldId}`, {});
    const hasChanged = get(this.state, `changedFields.${fieldId}`, false);
    const hasBoolean =
      get(this.props, 'hasBoolean', false) && filterField.hasBoolean;

    let dataRectriction = null;
    this.props.allowedDataCategories.forEach(f => {
      const [key, restriction] = f.split(':');
      if (key === filterField.id && restriction !== '*') {
        dataRectriction = restriction;
      }
    });

    if (filterField.id === 'contentType' && !this.props.showContentTypes) {
      return null;
    }

    if (filterField.id === 'network' && !this.props.showNetworks) {
      return null;
    }

    if (filterField.id === 'studio' && !this.props.showStudios) {
      return null;
    }

    return (
      <Filter
        key={filterField.id}
        hasChanged={hasChanged && hasChanged.length > 0}
        onChange={this.handleChange}
        selectedSegment={this.state.selectedSegment}
        fieldState={this.state.fields}
        onChangeBooleanSeparator={this.handleChangeBooleanSeparator}
        onUpdateFilterOptions={this.props.onUpdateFilterOptions}
        {...filterField}
        hasBoolean={hasBoolean}
        dataRectriction={dataRectriction}
        booleanSeparator={
          filterField.booleanSeparator === true
            ? 'OR'
            : filterField.booleanSeparator
        }
        restrictedCategories={this.props.restrictedCategories}
        dates={this.props.dates}
        rollupSupport={this.props.rollupSupport}
      />
    );
  };
  render() {
    const { fields, segments } = this.state;
    const { autoApply, requiredSegmentValues, segmentUUid } = this.props;
    const segment = get(
      segments.filter(s => s.uuid === segmentUUid),
      0,
      null
    );

    if (isEmpty(fields)) return null;

    const selectedFilters = {};

    forEach(keys(fields), k => {
      const value = get(fields, [k, 'value'], []);

      if (
        ((value !== null && value.length > 0) ||
          (typeof value === 'object' && keys(value).length > 0)) &&
        this.checkValidFilters(k) &&
        !this.props.hideFilters.includes(k)
      ) {
        selectedFilters[k] = fields[k];
      }
    });

    const showSaveSegment = () => {
      if (segment) {
        const formattedSegmentValueToCompare = {};
        JSON.parse(segment.value).forEach(v => {
          formattedSegmentValueToCompare[v.id] = v.value;
        });

        const formattedPropValueToCompare = {};
        Object.keys(this.props.fields).forEach(k => {
          const obj = this.props.fields[k];
          if (
            obj.value &&
            (obj.value.length || obj.value.value) &&
            k !== 'segments'
          ) {
            formattedPropValueToCompare[k] = obj.value;
          }
        });

        return !isEqual(
          formattedSegmentValueToCompare,
          formattedPropValueToCompare
        );
      }

      return segment === null || segmentUUid === null;
    };

    const unNamedSegments = filter(segments, segment => {
      const segmentName = get(segment, 'name', '');
      return !isEmpty(segment) && segmentName.substring(0, 7) === 'Segment';
    });

    const defaultSegmentName = unNamedSegments.length
      ? `Segment (${unNamedSegments.length})`
      : `Segment`;

    const visibleSelectedFilters = filter(
      selectedFilters,
      (selectedFilter, key) => {
        const isContentTypeAndHidden = !(
          key === 'contentType' && !this.props.showContentTypes
        );
        return isContentTypeAndHidden;
      }
    );
    return (
      <div className="tvt-filters-wrapper">
        <AudienceSegment
          fieldState={this.state.fields}
          requiredValues={requiredSegmentValues}
          name={defaultSegmentName}
          onChangeSegment={this.handleChange}
          handleApply={this.handleApply}
          availableFilters={this.props.fields}
          titleFields={getTitleFilterId(this.props.fields)}
        />
        <div className="tvt-filters tvt-filters--tabbed">
          <Tab.Container
            activeKey={this.state.selectedGroup}
            className="tvt-filters--tabbed"
            id="tvt-filters__tabs"
            onSelect={selectedGroup => this.setState({ selectedGroup })}
          >
            <>
              <div className="tvt-filters__tabs-nav clearfix">
                <span className="tvt-filters__title">
                  Filter Data By:
                  {this.props.tooltip ? (
                    <OverlayTrigger
                      placement="bottom"
                      overlay={
                        <Tooltip id="benchmark-bar__item-tooltip">
                          {this.props.tooltip}
                        </Tooltip>
                      }
                    >
                      <FontAwesomeIcon
                        className="benchmark-info-icon"
                        size="1x"
                        icon="info-circle"
                      />
                    </OverlayTrigger>
                  ) : null}
                </span>

                <Nav bsStyle="pills">
                  {map(this.state.groupings, this.renderGroup)}
                </Nav>

                {!autoApply && this.renderApply()}
              </div>

              <Tab.Content animation={false}>
                {map(this.state.groupings, this.renderGroupContent)}
              </Tab.Content>
            </>
          </Tab.Container>
        </div>
        {!isEmpty(visibleSelectedFilters) && (
          <ActiveFilters
            filters={visibleSelectedFilters}
            onClear={this.handleClear}
            onClearAll={this.handleClearAll}
            hasBool={this.props.hasBoolean}
            actionButtonFunc={this.onClickSaveFilters}
            actionButtonText="Save Filters"
            showActionButton={showSaveSegment()}
          />
        )}
      </div>
    );
  }
}

TabbedFilters.propTypes = {
  autoApply: PropTypes.bool,
  changedFields: PropTypes.objectOf(PropTypes.bool),
  fields: FilterSetType.isRequired,
  groupings: GroupingSetType.isRequired,
  hasBoolean: PropTypes.bool,
  hasChanges: PropTypes.bool,
  hideGroup: PropTypes.bool,
  hideFilters: PropTypes.arrayOf(PropTypes.string),
  loading: PropTypes.bool,
  showContentTypes: PropTypes.bool,
  showNetworks: PropTypes.bool,
  showStudios: PropTypes.bool,
  onApply: PropTypes.func.isRequired,
  onChange: PropTypes.func,
  onChangeBooleanSeparator: PropTypes.func,
  onClear: PropTypes.func.isRequired,
  onClearAll: PropTypes.func.isRequired,
  resetFilters: PropTypes.func.isRequired,
  onUpdateFilterOptions: PropTypes.func.isRequired,
  openAudienceSegmentWindow: PropTypes.func.isRequired,
  selectedSegment: PropTypes.shape({}).isRequired,
  segments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  segmentUUid: PropTypes.string,
  allowedDataCategories: PropTypes.arrayOf(PropTypes.string).isRequired,
  requiredSegmentValues: PropTypes.arrayOf(PropTypes.string),
  tooltip: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  restrictedCategories: PropTypes.array,
  rollupSupport: PropTypes.array,
  dates: PropTypes.array
};

TabbedFilters.defaultProps = {
  autoApply: false,
  changedFields: {},
  hasBoolean: false,
  hasChanges: false,
  hideGroup: false,
  hideFilters: [],
  loading: false,
  segmentUUid: null,
  showContentTypes: true,
  showNetworks: true,
  showStudios: true,
  onChange: () => {},
  onChangeBooleanSeparator: () => {},
  requiredSegmentValues: [],
  tooltip: '',
  restrictedCategories: [],
  rollupSupport: []
};

export default TabbedFilters;
